import { useState } from "react";
import { DotLegalSelectOption } from "@common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { useSecurityMeasuresQuery } from "@common/hooks/useSelectableItemQueries";
import { useValidator } from "@common/hooks/validation";
import { useTranslation } from "@localization/useTranslation";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery";
import { useLinkedControls } from "../../riskControls/RiskControls.hooks";
import { useRiskExposureConfig } from "../../riskExposure/useRiskExposureConfig";
import { MitigationPlanForm, MitigationPlanSaveModel } from "./TreatmentDialogs.types";

export const toLevelOptions = (levelNames: Array<string>): Array<DotLegalSelectOption> =>
    levelNames.map((name, index) => ({ id: (index + 1).toString(), name: `${index + 1} - ${name}` }));

export const emptyMitigationPlanForm: MitigationPlanForm = {
    name: "",
    description: "",
    responsibleId: null,
    deadline: null,
    expectedConsequence: null,
    expectedProbability: null,
    estimatedCost: "",
    expectedExposureValue: "",
    securityMeasureIds: [],
    frameworkControlIds: [],
};

export type MitigationPlanFormApi = ReturnType<typeof useMitigationPlanForm>;

// Everything the three mitigation dialogs (single, bulk by scenarios, bulk by entities) have in common: the
// form state, the option/query wiring for the shared fields, validation and the save payload for the shared
// part of their requests. Each dialog keeps what is genuinely its own: its mutation and snackbar, prefill,
// level options (their source differs per surface) and per-surface extras.
export function useMitigationPlanForm() {
    const { translateString } = useTranslation();

    const [form, setForm] = useState<MitigationPlanForm>(emptyMitigationPlanForm);
    const updateField = <K extends keyof MitigationPlanForm>(key: K, value: MitigationPlanForm[K]) => setForm((current) => ({ ...current, [key]: value }));

    const { exposureMode, exposureEnabled, exposureCurrencyCode, exposureScaleUpperBound } = useRiskExposureConfig();

    const exposureScaleOptions = Array.from({ length: exposureScaleUpperBound ?? 0 }, (_, index) => ({
        id: (index + 1).toString(),
        name: (index + 1).toString(),
    }));

    const responsibleQuery = useResponsibleQuery();
    const responsibleOptions = responsibleQuery.data(form.responsibleId) ?? [];

    const securityMeasuresQuery = useSecurityMeasuresQuery(true, true);
    const securityMeasureOptions = securityMeasuresQuery.data ?? [];

    // The full selectable set; dialogs that must pre-exclude a scenario's head controls derive their own
    // filtered list from this.
    const { availableControls: allControls, showLinkedControls } = useLinkedControls(form.frameworkControlIds);

    const validation = useValidator<MitigationPlanForm>((validators) => ({
        name: validators.validateNotEmpty((x) => x.name, translateString("name")),
        description: validators.validateNotEmpty((x) => x.description, translateString("action")),
        expectedConsequence: validators.validateNotEmpty((x) => x.expectedConsequence, translateString("consequenceAfterMitigation")),
        expectedProbability: validators.validateNotEmpty((x) => x.expectedProbability, translateString("probabilityAfterMitigation")),
    }));

    validation.check(form);

    // Exposure fields are only sent when the tenant has exposure enabled; an untouched field means "not set".
    const toExposureNumber = (value: string) => (exposureEnabled && value !== "" ? Number(value) : null);

    const toSavePayload = (): MitigationPlanSaveModel => ({
        name: form.name,
        description: form.description,
        responsibleId: form.responsibleId,
        deadline: form.deadline,
        expectedConsequence: Number(form.expectedConsequence),
        expectedProbability: Number(form.expectedProbability),
        estimatedCost: toExposureNumber(form.estimatedCost),
        expectedExposureValue: toExposureNumber(form.expectedExposureValue),
        securityMeasureIds: form.securityMeasureIds,
        frameworkControlIds: form.frameworkControlIds,
    });

    return {
        form,
        setForm,
        validation,
        toSavePayload,
        exposureMode,
        exposureEnabled,
        exposureCurrencyCode,
        exposureScaleUpperBound,
        exposureScaleOptions,
        allControls,
        showLinkedControls,
        name: form.name,
        setName: (value: string) => updateField("name", value),
        description: form.description,
        setDescription: (value: string) => updateField("description", value),
        isLoadingSecurityMeasures: securityMeasuresQuery.isLoading,
        securityMeasureOptions,
        securityMeasureIds: form.securityMeasureIds,
        setSecurityMeasureIds: (ids: Array<string>) => updateField("securityMeasureIds", ids),
        isLoadingResponsibles: responsibleQuery.isLoading,
        responsibleOptions,
        responsibleId: form.responsibleId,
        setResponsibleId: (value: string | null) => updateField("responsibleId", value),
        deadline: form.deadline,
        setDeadline: (value: Date | null) => updateField("deadline", value),
        expectedConsequence: form.expectedConsequence,
        setExpectedConsequence: (value: string | null) => updateField("expectedConsequence", value),
        expectedProbability: form.expectedProbability,
        setExpectedProbability: (value: string | null) => updateField("expectedProbability", value),
        estimatedCost: form.estimatedCost,
        setEstimatedCost: (value: string) => updateField("estimatedCost", value),
        expectedExposureValue: form.expectedExposureValue,
        setExpectedExposureValue: (value: string) => updateField("expectedExposureValue", value),
        frameworkControlIds: form.frameworkControlIds,
        setFrameworkControlIds: (ids: Array<string>) => updateField("frameworkControlIds", ids),
    };
}
